import * as React from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Select, ISelectOption, InputLabel } from 'atoms';
import { mq } from 'components/constants/breakpoints';
import { useGetCoursesForTeacher } from 'core/hooks';

import { ICourse } from 'types';

export interface ITeachersFilterBar {
  course: ISelectOption<string> | null;
  rating: ISelectOption<number> | null;
  availability: ISelectOption<string> | null;
  onCourseChange: (option: ISelectOption<string> | null) => void;
  onRatingChange: (option: ISelectOption<number> | null) => void;
  onAvailabilityChange: (option: ISelectOption<string> | null) => void;
  /**
   * Фильтр «рейтинг от» — по служебному полю; методисту рейтинг не
   * показывается, и фильтра у него нет.
   */
  showRating?: boolean;
}

/**
 * Filter bar for the teachers table (spec 0007 rev.): course (qualified OR
 * taught), minimum rating, and availability status. «Все» clears a filter.
 */
export const TeachersFilterBar = memo(
  ({
    course,
    rating,
    availability,
    onCourseChange,
    onRatingChange,
    onAvailabilityChange,
    showRating = true,
  }: ITeachersFilterBar) => {
    const { t } = useTranslation();
    const { data: courses, loading } = useGetCoursesForTeacher();

    const allLabel = `${t('table.teachers.filters.all')}`;

    const courseOptions: ISelectOption<string>[] = useMemo(
      () => [
        { label: allLabel, value: '' },
        ...(courses?.map((item: ICourse) => ({
          label: item.name,
          value: item.id,
        })) || []),
      ],
      [courses, allLabel],
    );

    const ratingOptions: ISelectOption<number>[] = useMemo(
      () => [
        { label: allLabel, value: 0 },
        ...[1, 2, 3, 4, 5].map((value) => ({
          label: `${t('table.teachers.filters.ratingFrom')} ${value}`,
          value,
        })),
      ],
      [allLabel, t],
    );

    const availabilityOptions: ISelectOption<string>[] = useMemo(
      () => [
        { label: allLabel, value: '' },
        {
          label: `${t('table.teachers.availability.free')}`,
          value: 'free',
        },
        {
          label: `${t('table.teachers.availability.teaching')}`,
          value: 'teaching',
        },
      ],
      [allLabel, t],
    );

    return (
      <Wrapper>
        <Field>
          <InputLabel isOptional>
            {t('table.teachers.filters.course')}
          </InputLabel>
          <Select
            type="default"
            placeholder={allLabel}
            options={courseOptions}
            value={course}
            loading={loading}
            onChange={(value) =>
              onCourseChange(value as ISelectOption<string> | null)
            }
          />
        </Field>
        {showRating ? (
          <Field>
            <InputLabel isOptional>
              {t('table.teachers.filters.rating')}
            </InputLabel>
            <Select
              type="default"
              placeholder={allLabel}
              options={ratingOptions}
              value={rating}
              onChange={(value) =>
                onRatingChange(value as ISelectOption<number> | null)
              }
            />
          </Field>
        ) : null}
        <Field>
          <InputLabel isOptional>
            {t('table.teachers.filters.status')}
          </InputLabel>
          <Select
            type="default"
            placeholder={allLabel}
            options={availabilityOptions}
            value={availability}
            onChange={(value) =>
              onAvailabilityChange(value as ISelectOption<string> | null)
            }
          />
        </Field>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 24px;

  /* ТЗ 0121: три фильтра по 240px шире телефона и планшета — переносятся. */
  ${mq.belowDesktop} {
    flex-wrap: wrap;
    gap: 12px 16px;
  }
`;

const Field = styled.div`
  width: 240px;

  ${mq.mobile} {
    width: 100%;
  }
`;
